'use client';

import {
  AnimatePresence,
  type HTMLMotionProps,
  motion,
  type SpringOptions,
  useMotionValue,
  useSpring,
} from 'motion/react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../utils';

interface CursorFollowContextType {
  isActive: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

const CursorFollowContext = React.createContext<
  CursorFollowContextType | undefined
>(undefined);

const useCursorFollow = (): CursorFollowContextType => {
  const context = React.useContext(CursorFollowContext);
  if (!context) {
    throw new Error(
      'useCursorFollow must be used within a CursorFollowProvider',
    );
  }
  return context;
};

type CursorFollowProviderProps = React.ComponentProps<'div'> & {
  children: React.ReactNode;
  delay?: number;
};

const CursorFollowProvider = React.forwardRef<
  HTMLDivElement,
  CursorFollowProviderProps
>(function CursorFollowProvider({ children, delay = 0, ...props }, ref) {
  const [isActive, setIsActive] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();
  React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleMouseEnter = () => {
      if (delay > 0) {
        timerRef.current = setTimeout(() => setIsActive(true), delay);
      } else {
        setIsActive(true);
      }
    };

    const handleMouseLeave = () => {
      clearTimeout(timerRef.current);
      setIsActive(false);
    };

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timerRef.current);
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [delay]);

  return (
    <CursorFollowContext.Provider value={{ isActive, containerRef }}>
      <div data-slot="cursor-follow-provider" ref={containerRef} {...props}>
        {children}
      </div>
    </CursorFollowContext.Provider>
  );
});

type Align =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'right'
  | 'center';

type CursorFollowProps = Omit<HTMLMotionProps<'div'>, 'ref'> & {
  sideOffset?: number;
  align?: Align;
  transition?: SpringOptions;
  children: React.ReactNode;
};

const CursorFollow = React.forwardRef<HTMLDivElement, CursorFollowProps>(
  function CursorFollow(
    {
      sideOffset = 6,
      align = 'bottom-right',
      children,
      className,
      style,
      transition = { stiffness: 1000, damping: 80, bounce: 0 },
      ...props
    },
    ref,
  ) {
    const { isActive, containerRef } = useCursorFollow();
    const followRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => followRef.current as HTMLDivElement);

    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const springX = useSpring(x, transition);
    const springY = useSpring(y, transition);

    const alignRef = React.useRef(align);
    alignRef.current = align;
    const sideOffsetRef = React.useRef(sideOffset);
    sideOffsetRef.current = sideOffset;
    const needsJump = React.useRef(true);

    React.useEffect(() => {
      if (!isActive) {
        needsJump.current = true;
      }
    }, [isActive]);

    React.useEffect(() => {
      const el = containerRef.current;
      if (!el) return;

      const handleCursorPosition = (e: MouseEvent) => {
        const rect = followRef.current?.getBoundingClientRect();
        const width = rect?.width ?? 0;
        const height = rect?.height ?? 0;
        const currentAlign = alignRef.current;
        const currentOffset = sideOffsetRef.current;

        let offsetX = 0;
        let offsetY = 0;

        switch (currentAlign) {
          case 'center':
            offsetX = width / 2;
            offsetY = height / 2;
            break;
          case 'top':
            offsetX = width / 2;
            offsetY = height + currentOffset;
            break;
          case 'top-left':
            offsetX = width + currentOffset;
            offsetY = height + currentOffset;
            break;
          case 'top-right':
            offsetX = -currentOffset;
            offsetY = height + currentOffset;
            break;
          case 'bottom':
            offsetX = width / 2;
            offsetY = -currentOffset;
            break;
          case 'bottom-left':
            offsetX = width + currentOffset;
            offsetY = -currentOffset;
            break;
          case 'bottom-right':
            offsetX = -currentOffset;
            offsetY = -currentOffset;
            break;
          case 'left':
            offsetX = width + currentOffset;
            offsetY = height / 2;
            break;
          case 'right':
            offsetX = -currentOffset;
            offsetY = height / 2;
            break;
        }

        const targetX = e.clientX - offsetX;
        const targetY = e.clientY - offsetY;

        /**
         *
         * When the cursor first enters the container, isActive is set to true, which sets
         * needsJump to true.
         *
         * When the cursor starts moving when isActive is true, we force the `motion useSpring`
         * to jump to the current cursor position. This prevents the popover from animating from
         * th top-left corner of the screen to the cursor position.
         */
        if (needsJump.current) {
          springX.jump(targetX);
          springY.jump(targetY);
          needsJump.current = false;
        } else {
          x.set(targetX);
          y.set(targetY);
        }
      };

      el.addEventListener('mousemove', handleCursorPosition);
      el.addEventListener('mouseenter', handleCursorPosition);

      return () => {
        el.removeEventListener('mousemove', handleCursorPosition);
        el.removeEventListener('mouseenter', handleCursorPosition);
      };
    }, [containerRef, x, y, springX, springY]);

    return createPortal(
      <AnimatePresence>
        {isActive && (
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className={cn(
              'pointer-events-none z-[9998] fixed',
              'bg-white border border-border rounded-md p-1 shadow-lg',
              className,
            )}
            data-slot="cursor-follow"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            ref={followRef}
            style={{ top: springY, left: springX, ...style }}
            transition={{
              opacity: { duration: 0.2 },
            }}
            {...props}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  },
);

export {
  CursorFollowProvider,
  CursorFollow,
  useCursorFollow,
  type CursorFollowContextType,
  type CursorFollowProviderProps,
  type CursorFollowProps,
};
